// ============================================================================
// src/layer/layerGroupPalette.ts — R06 group color for Layer groups
// ----------------------------------------------------------------------------
// Layer groups carry no group number, so the palette index is a RENDERING
// ordinal derived from the canonical ppp-layer-groups-v1 order: the first
// group in that list is --group-1, the second --group-2, and so on, mod 6. It
// is computed once over the whole canonical list — never over the rows a page
// happens to be showing — so a group keeps the same color in a list, a record
// title and a card, whatever is filtered. Nothing is persisted and no row
// gains a property; the ordinal follows the canonical order, so it moves only
// when that order does.
//
// A group whose canonical status is not exactly `active` is archived and takes
// the archived gray (Table H). A bare-string legacy entry carries no status and
// makes no archived claim, so it keeps its ordinal color. A name the canonical
// list does not hold has no color at all: callers render no dot and no tint.
// ============================================================================
import {groupColor} from '../shared/GroupPillPicker.js';
import {
  LAYER_GROUP_STATUS_INACTIVE,
  buildLayerGroupStatusIndex,
  normalizeLayerGroupName,
} from '../lib/layerGroupStatus.js';

export type LayerGroupPalette = ReadonlyMap<string, string>;

const EMPTY_PALETTE: LayerGroupPalette = new Map();

/** Normalized group name -> its dot color, over the canonical list in order. */
export function buildLayerGroupPalette(canonicalGroups: unknown): LayerGroupPalette {
  if (!Array.isArray(canonicalGroups) || canonicalGroups.length === 0) return EMPTY_PALETTE;
  // Archived is read off the same index every Layer status decision uses, so a
  // duplicate name that is inactive anywhere is gray here too (fail closed).
  const statusIndex = buildLayerGroupStatusIndex(canonicalGroups);
  const palette = new Map<string, string>();
  let ordinal = 0;
  for (const entry of canonicalGroups) {
    const isRow = Boolean(entry) && typeof entry === 'object';
    const key = normalizeLayerGroupName(isRow ? (entry as {name?: unknown}).name : entry);
    // A nameless row makes no claim, and a duplicate name keeps the first
    // place it holds — neither consumes an ordinal.
    if (!key || palette.has(key)) continue;
    const archived = statusIndex.get(key) === LAYER_GROUP_STATUS_INACTIVE;
    palette.set(key, archived ? 'var(--group-archived)' : groupColor(ordinal));
    ordinal += 1;
  }
  return palette;
}

/** The dot color for a group name, or null when the canonical list lacks it. */
export function layerGroupColor(palette: LayerGroupPalette, name: unknown): string | null {
  const key = normalizeLayerGroupName(name);
  return (key && palette.get(key)) || null;
}

/** R06: a group's own card takes a light tint of its color; archived stays neutral. */
export function layerGroupTint(color: string | null, pct = 6): string | null {
  if (!color || color === 'var(--group-archived)') return null;
  return 'color-mix(in srgb, ' + color + ' ' + pct + '%, var(--surface))';
}
